import {i18n} from '@online-offer/i18n';
import {Separator} from 'platform/components';
import {Heading, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {parseDate} from 'shared/web';

import {OnlineOfferAlternative} from '../../../api/types/onlineOfferAlternativesTypes';
import {CardOo} from '../../../components/CardOo/CardOo';
import {useFormatAlternativeTotal} from '../../../hooks/useFormatAlternativeTotal';
import {buildComparisonRows} from '../../../utils/buildComparisonRows';
import {getComparisonRowLabel} from '../../../utils/getComparisonRowLabel';
import {translateWithFallback as t} from '../../../utils/translateWithFallback';
import {useFormatComparisonValue} from '../../AlternativesComparison/hooks/useFormatComparisonValue';
import {ReservationRow} from '../../ReservationCard/ReservationRow';

const EMPTY = '—';

interface AlternativePriceCardProps {
  alternative: OnlineOfferAlternative;
}

/** Price breakdown of an alternative, in the look of the deal vehicle's price card. */
export function AlternativePriceCard(props: AlternativePriceCardProps) {
  const formatTotal = useFormatAlternativeTotal();
  const formatValue = useFormatComparisonValue();
  const formatDateTime = useDateTimeFormatter();

  const priceRows = buildComparisonRows([props.alternative])
    .filter((row) => row.group === 'price')
    .map((row) => ({row, value: row.values[0] ? formatValue(row.values[0]) : EMPTY}))
    .filter(({value}) => value !== EMPTY);

  return (
    <CardOo backgroundColor="semantic.bg.subtle">
      <VStack spacing={5}>
        <ReservationRow
          label={i18n.t('reservationCard.totalPrice')}
          labelProps={{alternative: true}}
          value={<Heading size={3}>{formatTotal(props.alternative)}</Heading>}
        />
        <Show when={priceRows.length > 0}>
          <Separator spacing={0} />
          <VStack spacing={2}>
            {priceRows.map(({row, value}) => (
              <ReservationRow
                key={row.id}
                label={getComparisonRowLabel(row)}
                value={value}
                valueProps={row.id === 'discount' ? {color: 'success'} : undefined}
              />
            ))}
          </VStack>
        </Show>
        <Show when={isNotNil(props.alternative.validUntil)}>
          <Text size="xSmall" color="secondary">
            {t('alternatives.detail.validUntil', 'Offer valid until {date}', {
              date: props.alternative.validUntil
                ? formatDateTime('dateShort', parseDate(props.alternative.validUntil))
                : '',
            })}
          </Text>
        </Show>
      </VStack>
    </CardOo>
  );
}
